class InfoMoMediaSlider {
  constructor(container) {
    this.container = container;
    this.slides = [...container.querySelectorAll('.info-mo-media-slider__slide')];
    this.paginationItems = [...container.querySelectorAll('.info-mo-media-slider__pagination-item')];
    this.progressBars = [...container.querySelectorAll('.info-mo-media-slider__pagination-progress')];
    this.prevButton = container.querySelector('[data-info-slider-prev]');
    this.nextButton = container.querySelector('[data-info-slider-next]');
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.slideDuration = 8000;
    this.progress = 0;
    this.lastTime = null;
    this.raf = null;
    this.isPaused = false;
    this.isTabHidden = false;
    this.isTransitioning = false;
    this.autoplay = container.dataset.autoplay !== 'false';
    this.isRTL = document.documentElement.dir === 'rtl' || document.body?.dataset.rtl === 'true';
    this.handlePrev = this.previousSlide.bind(this);
    this.handleNext = this.nextSlide.bind(this);
    this.init();
  }

  init() {
    if (this.totalSlides <= 1) return;

    this.slides.forEach((slide, index) => {
      slide.classList.toggle('info-mo-media-slider__slide--active', index === 0);
    });

    this.bindEvents();
    this.syncVideos(0);

    if (this.autoplay) {
      this.startAutoPlay();
    }
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.isRTL ? this.handleNext : this.handlePrev);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.isRTL ? this.handlePrev : this.handleNext);
    }

    this.paginationItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        if (this.currentSlide !== index) this.goToSlide(index);
      });
    });

    this.container.addEventListener('mouseenter', () => this.pauseAutoPlay());
    this.container.addEventListener('mouseleave', () => this.resumeAutoPlay());
    this.container.addEventListener('focusin', () => this.pauseAutoPlay());
    this.container.addEventListener('focusout', () => this.resumeAutoPlay());

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.isTabHidden = true;
        this.pauseAutoPlay();
      } else {
        this.isTabHidden = false;
        this.resumeAutoPlay();
      }
    });

    if (this.isMobileDevice()) {
      this.bindSwipeEvents();
    }
  }

  isMobileDevice() {
    return window.innerWidth <= 768;
  }

  bindSwipeEvents() {
    let startX = 0;
    let endX = 0;

    this.container.addEventListener('touchstart', (event) => {
      startX = event.changedTouches[0].screenX;
    }, { passive: true });

    this.container.addEventListener('touchend', (event) => {
      endX = event.changedTouches[0].screenX;
      const diff = startX - endX;
      if (Math.abs(diff) < 40) return;
      if (diff > 0) {
        this.isRTL ? this.previousSlide() : this.nextSlide();
      } else {
        this.isRTL ? this.nextSlide() : this.previousSlide();
      }
    }, { passive: true });
  }

  showSlide(index) {
    this.pauseVideos();
    this.currentSlide = index;
    this.progress = 0;
    this.lastTime = null;

    this.slides.forEach((slide, i) => {
      slide.classList.toggle('info-mo-media-slider__slide--active', i === index);
    });

    this.paginationItems.forEach((item, i) => {
      item.classList.toggle('info-mo-media-slider__pagination-item--active', i === index);
    });

    this.progressBars.forEach((bar) => {
      bar.style.width = '0%';
      bar.style.transition = 'none';
    });

    const activeBar = this.progressBars[index];
    if (activeBar) {
      activeBar.offsetHeight;
      activeBar.style.transition = 'width 0.07s linear';
    }

    this.syncVideos(index);
  }

  syncVideos(index) {
    this.slides.forEach((slide, i) => {
      slide.querySelectorAll('video').forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        if (i === index) {
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      });
    });
  }

  pauseVideos() {
    this.slides.forEach((slide) => {
      slide.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    });
  }

  goToSlide(index) {
    this.showSlide(index);
    this.resetAutoPlay();
  }

  nextSlide() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.showSlide((this.currentSlide + 1) % this.totalSlides);
    this.resetAutoPlay();
    window.setTimeout(() => {
      this.isTransitioning = false;
    }, 80);
  }

  previousSlide() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.showSlide((this.currentSlide - 1 + this.totalSlides) % this.totalSlides);
    this.resetAutoPlay();
    window.setTimeout(() => {
      this.isTransitioning = false;
    }, 80);
  }

  resetAutoPlay() {
    this.progress = 0;
    this.lastTime = null;
    if (!this.isPaused && !this.isTabHidden && this.autoplay) {
      this.startAutoPlay();
    }
  }

  startAutoPlay() {
    if (!this.autoplay || this.isPaused || this.isTabHidden) return;

    window.cancelAnimationFrame(this.raf);
    this.lastTime = null;
    this.progress = 0;

    const animate = (timestamp) => {
      if (this.isPaused || this.isTabHidden || !this.autoplay) return;

      if (!this.lastTime) this.lastTime = timestamp;

      const delta = timestamp - this.lastTime;
      this.progress += delta / this.slideDuration;

      const activeBar = this.progressBars[this.currentSlide];
      if (activeBar) {
        activeBar.style.width = Math.min(this.progress * 100, 100) + '%';
      }

      if (this.progress >= 1) {
        this.nextSlide();
        return;
      }

      this.lastTime = timestamp;
      this.raf = window.requestAnimationFrame(animate);
    };

    this.raf = window.requestAnimationFrame(animate);
  }

  pauseAutoPlay() {
    this.isPaused = true;
    window.cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  resumeAutoPlay() {
    if (this.isTabHidden || !this.autoplay) return;
    this.isPaused = false;
    this.lastTime = null;
    window.requestAnimationFrame(() => this.startAutoPlay());
  }
}

function initInfoMoMediaSliders() {
  document.querySelectorAll('[data-info-media-slider]').forEach((container) => {
    if (container.dataset.infoMediaSliderInit === 'true') return;
    container.dataset.infoMediaSliderInit = 'true';
    new InfoMoMediaSlider(container);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfoMoMediaSliders);
} else {
  initInfoMoMediaSliders();
}

document.addEventListener('shopify:section:load', initInfoMoMediaSliders);
